import { type CustomObjectInfo, type Group, OBJECT_NAMES, type ObjectDef, type ObjectName, type Pipeline, type PipelineStage, type Queue, type RecordTypeInfo, type Role, type SessionInfo, type UserSummary } from '@crm/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext } from 'react';
import { api } from './api';

const SessionContext = createContext<SessionInfo | null>(null);

export function SessionProvider({ value, children }: { value: SessionInfo; children: ReactNode }) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionInfo {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession must be used inside SessionProvider');
  return s;
}

/** This user's definition of an object: custom fields included, fields hidden by field-level security left out. */
export function useObjectDef(name: string | undefined): ObjectDef | undefined {
  const { schema } = useSession();
  return name && (OBJECT_NAMES as readonly string[]).includes(name) ? schema[name as ObjectName] : undefined;
}

/** A custom object the user can read (by API name), or undefined. */
export function useCustomObject(apiName: string | undefined): CustomObjectInfo | undefined {
  const { customObjects } = useSession();
  return customObjects.find((o) => o.def.apiName === apiName);
}

/** Record types of a standard object (inactive ones included, flagged). */
export function useRecordTypes(object: string): RecordTypeInfo[] {
  const { recordTypes } = useSession();
  return recordTypes.filter((r) => r.objectName === object);
}

export function useStages() {
  return useQuery({
    queryKey: ['meta', 'stages'],
    queryFn: () => api<{ stages: PipelineStage[] }>('/api/meta/stages').then((r) => r.stages),
    staleTime: 5 * 60_000,
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ['meta', 'users'],
    queryFn: () => api<{ users: UserSummary[] }>('/api/meta/users').then((r) => r.users),
    staleTime: 5 * 60_000,
  });
}

export function useRoles() {
  return useQuery({ queryKey: ['meta', 'roles'], queryFn: () => api<{ roles: Role[] }>('/api/meta/roles').then((r) => r.roles), staleTime: 60_000 });
}

export function useGroups() {
  return useQuery({ queryKey: ['meta', 'groups'], queryFn: () => api<{ groups: Group[] }>('/api/meta/groups').then((r) => r.groups), staleTime: 60_000 });
}

export function usePipelines() {
  return useQuery({ queryKey: ['meta', 'pipelines'], queryFn: () => api<{ pipelines: Pipeline[] }>('/api/meta/pipelines').then((r) => r.pipelines), staleTime: 5 * 60_000 });
}

export function useQueues() {
  return useQuery({ queryKey: ['meta', 'queues'], queryFn: () => api<{ queues: Queue[] }>('/api/meta/queues').then((r) => r.queues), staleTime: 60_000 });
}

/** Projects (main projects deals belong to), for pickers. */
export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => api<{ projects: { id: string; name: string; procurementStatus: 'tender' | 'on_hand' }[] }>('/api/projects').then((r) => r.projects),
    staleTime: 30_000,
  });
}

/** Active campaigns, for pickers. Empty for people without campaign access. */
export function useCampaignOptions() {
  const { permissions } = useSession();
  return useQuery({
    queryKey: ['campaigns', 'options'],
    enabled: permissions.objects.campaign?.read ?? false,
    queryFn: () => api<{ campaigns: { id: string; name: string }[] }>('/api/campaigns?active=true').then((r) => r.campaigns),
    staleTime: 60_000,
  });
}
